import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/pages/portal/api";

const NO_GUARANTEE =
  "Preliminary approval, a requested deployment window, or a tentative vehicle assignment does not guarantee " +
  "equipment placement, availability, exclusivity, insurance approval, or revenue.";

export const INSURANCE_COVERAGE_NOTE =
  "Insurance is covered by Xtreme Exotics: every Jet Car operates under Xtreme Exotics' commercial watercraft/" +
  "rental liability and physical-damage coverage (XINSURANCE fleet-partnership agreements), and the partner is " +
  "added as an additional insured. The partner contributes 45% of any deductible, capped at $2,500 — " +
  "Xtreme Exotics covers the balance.";

const JET_CARS = ["Sport Coupe Style", "Sedan Style", "SUV Style", "No preference"];
const US_STATES = ["FL", "AL", "GA", "SC", "NC", "TX", "LA", "MS", "Other"];

const empty = {
  business_name: "", contact_name: "", email: "", phone: "", city_state: "", state: "",
  current_services: "", operating_location: "", waterfront_address: "",
  preferred_season: "", earliest_deployment: "", latest_deployment: "", expected_months: "",
  preferred_jet_car: "", storage_dock_ramp: "", staffing_training: "",
  permit_status: "", insurance_readiness: "", booking_payment_system: "",
  estimated_ready_date: "", delay_reasons: "", alternative_dates: "", fax_number: "",
};

const FIELDS = [
  { section: "Business & Contact", fields: [
    ["business_name", "Business name"], ["contact_name", "Contact name"],
    ["email", "Email", "email"], ["phone", "Phone", "tel"],
    ["city_state", "City, State"], ] },
  { section: "Current Operation", fields: [
    ["current_services", "Current services & rental activities", null, true],
    ["operating_location", "Operating location (marina / waterfront name)"],
    ["waterfront_address", "Waterfront address"], ] },
  { section: "Deployment Window", fields: [
    ["preferred_season", "Preferred deployment season (e.g. Spring 2027)"],
    ["earliest_deployment", "Earliest acceptable deployment date", "date"],
    ["latest_deployment", "Latest acceptable deployment date", "date"],
    ["expected_months", "Expected operating months (e.g. March – October)"],
    ["alternative_dates", "Alternative deployment dates (optional)", null, false, true], ] },
  { section: "Operations & Readiness", note: INSURANCE_COVERAGE_NOTE, fields: [
    ["storage_dock_ramp", "Storage, dock, ramp & water access", null, true],
    ["staffing_training", "Staffing & training availability", null, true],
    ["permit_status", "Permit & licensing status"],
    ["insurance_readiness", "Insurance readiness"],
    ["booking_payment_system", "Booking & payment system (e.g. FareHarbor + Stripe)"],
    ["estimated_ready_date", "Estimated date fully ready", "date"],
    ["delay_reasons", "Reasons for delaying deployment", null, true], ] },
];

export default function PipelineApply() {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const navigate = useNavigate();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const requiredKeys = Object.keys(empty).filter((k) => !["alternative_dates", "fax_number"].includes(k));
  const valid = requiredKeys.every((k) => String(form[k]).trim()) && form.email.includes("@")
    && form.earliest_deployment && form.latest_deployment;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pipeline/intake", form);
      setDone(data.pipeline_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed — please try again");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="bg-ink" data-testid="pipeline-success">
        <Navbar />
        <main className="grid min-h-screen place-items-center px-5 py-32">
          <div className="card-line w-full max-w-lg rounded-2xl bg-white p-10 text-center">
            <CheckCircle2 size={40} className="mx-auto text-emerald-600" />
            <h1 className="mt-5 font-display text-2xl font-extrabold text-offwhite">You're in the future-season pipeline</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted">
              Reference <span className="font-mono font-bold text-offwhite" data-testid="pipeline-ref">{done}</span>.
              Create your portal account with this email to track your pipeline stage, upload documents, and get
              countdown reminders as your season approaches.
            </p>
            <p className="mt-3 rounded-lg border border-black/[0.08] bg-surface/60 p-3 text-[11px] text-muted">{NO_GUARANTEE}</p>
            <button onClick={() => navigate("/login")} className="btn-primary mt-6 w-full" data-testid="pipeline-create-account-btn">
              Create Account / Sign In
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-ink" data-testid="pipeline-apply">
      <Navbar />
      <main className="px-5 pb-24 pt-[140px]">
        <div className="mx-auto max-w-2xl">
          <p className="eyebrow">Future Deployment · Seasonal Partner Pipeline</p>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-offwhite" data-testid="pipeline-apply-title">
            Plan for a Future Season
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-muted">
            For qualified watersports, marina, boat-rental, resort and Jet Ski operators who want a Jet Car but are
            not ready this season. You'll get a pipeline dashboard, a readiness checklist, and automated countdown
            reminders at 120 / 90 / 60 / 30 / 14 days before your requested deployment date.
          </p>
          <p className="mt-4 rounded-lg border border-black/[0.08] bg-white p-3 text-[11px] leading-relaxed text-muted" data-testid="pipeline-no-guarantee">{NO_GUARANTEE}</p>

          <div className="mt-8 space-y-8">
            {FIELDS.map((sec) => (
              <div key={sec.section} className="card-line rounded-2xl bg-white p-7" data-testid={`pipeline-section-${sec.section.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <h2 className="font-display text-base font-extrabold text-offwhite">{sec.section}</h2>
                {sec.note && (
                  <p className="mt-2 rounded-lg border border-aqua/30 bg-aqua/[0.07] p-3 text-[11px] leading-relaxed text-muted" data-testid="pipeline-insurance-note">{sec.note}</p>
                )}
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  {sec.fields.map(([k, label, type, full, optional]) => (
                    <div key={k} className={full ? "sm:col-span-2" : ""}>
                      <label className="field-label" htmlFor={`pl-${k}`}>{label}{optional ? " (optional)" : ""}</label>
                      {full ? (
                        <textarea id={`pl-${k}`} className="field min-h-[80px]" value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`pl-${k}`} />
                      ) : (
                        <input id={`pl-${k}`} type={type || "text"} className="field" value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`pl-${k}`} />
                      )}
                    </div>
                  ))}
                  {sec.section === "Business & Contact" && (
                    <div>
                      <label className="field-label" htmlFor="pl-state">State</label>
                      <select id="pl-state" className="field" value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="pl-state">
                        <option value="">Select…</option>
                        {US_STATES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                  )}
                  {sec.section === "Deployment Window" && (
                    <div>
                      <label className="field-label" htmlFor="pl-jet-car">Preferred Jet Car type (not guaranteed)</label>
                      <select id="pl-jet-car" className="field" value={form.preferred_jet_car} onChange={(e) => set("preferred_jet_car", e.target.value)} data-testid="pl-jet-car">
                        <option value="">Select…</option>
                        {JET_CARS.map((j) => <option key={j}>{j}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* honeypot */}
            <input type="text" value={form.fax_number} onChange={(e) => set("fax_number", e.target.value)}
              tabIndex={-1} autoComplete="off" aria-hidden="true"
              className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0" />

            <button onClick={submit} disabled={!valid || busy}
              className="btn-primary w-full !py-4 disabled:opacity-50" data-testid="pipeline-submit-btn">
              {busy ? <Loader2 size={16} className="mx-auto animate-spin" /> : "Submit Future-Season Application"}
            </button>
            <p className="text-center text-xs text-muted">
              Ready now? <Link to="/fleet-partner-application" className="font-semibold text-aqua hover:underline">Apply for this season instead</Link>
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
